import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { HEURES, toYmd } from '../../utils/planningTime';
import './MiniGrid.css';

// Nom AVANT prénom, comme dans PlanningAtelier (demande métier du 2026-08-09).
const getStaffLabel = (s) => [s.nom, s.prenom].filter(Boolean).join(' ') || s.nom || `#${s.id}`;
// Colonnes resserrées : nom seul (le prénom déborderait), tronqué en CSS.
const getShortLabel = (s) => s.nom || getStaffLabel(s);

const shiftDate = (iso, delta) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return toYmd(d);
};

const formatDateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

const JOUR_COL = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const toHM = (t) => (t ? String(t).slice(0, 5) : null);

// Aperçu compact et glisser-déposer du planning d'atelier (mécaniciens), embarqué
// dans la modale d'intervention. Trois états seulement — Libre (cliquable), Occupé,
// Ce RDV — pour faciliter la prise de rendez-vous par la réception. Voir
// MiniReceptionGrid pour le détail de la navigation jour par jour.
// `currents` : les créneaux EN COURS DE SAISIE dans le formulaire, dessinés en
// « fantômes » à la position saisie. `groupes` : la liste COMPLÈTE de la
// concession groupée par atelier ([{atelier, mecas}]) — quand elle est fournie,
// un sélecteur d'atelier apparaît dans l'aperçu et pilote les colonnes affichées.
export default function MiniAtelierGrid({
  date, mecaniciens = [], groupes = null, interventions = [], heures: heuresProp = null,
  currentInterventionId, current = null, currents = null, onMove, onPickSlot,
}) {
  const heures = (Array.isArray(heuresProp) && heuresProp.length > 0) ? heuresProp : HEURES;
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [viewDate, setViewDate] = useState(date);
  const [remoteInterventions, setRemoteInterventions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [pauses, setPauses] = useState([]);

  // ── Sélecteur d'atelier (point 1) ────────────────────────────────────────
  const ateliers = useMemo(
    () => (Array.isArray(groupes) ? groupes.filter(g => Array.isArray(g.mecas) && g.mecas.length) : null),
    [groupes]);
  // Défaut : l'atelier qui contient les mécaniciens déjà affichés (sinon le 1er).
  const atelierDefaut = useMemo(() => {
    if (!ateliers || !ateliers.length) return null;
    const firstId = mecaniciens[0]?.id;
    const found = firstId ? ateliers.find(g => g.mecas.some(m => m.id === firstId)) : null;
    return (found || ateliers[0]).atelier;
  }, [ateliers, mecaniciens]);
  const [atelierSel, setAtelierSel] = useState(atelierDefaut);
  useEffect(() => { setAtelierSel(atelierDefaut); }, [atelierDefaut]);

  const mecasAffiches = useMemo(() => {
    if (ateliers && atelierSel != null) {
      const g = ateliers.find(x => x.atelier === atelierSel);
      if (g) return g.mecas;
    }
    return mecaniciens;
  }, [ateliers, atelierSel, mecaniciens]);

  useEffect(() => { setViewDate(date); setRemoteInterventions(null); }, [date]);

  // Interventions des AUTRES jours (le jour du formulaire vient des props).
  useEffect(() => {
    if (!viewDate || viewDate === date) { setRemoteInterventions(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get('/planning/interventions', { params: { date_from: viewDate, date_to: viewDate } })
      .then(r => { if (!cancelled) setRemoteInterventions(r.data.interventions || []); })
      .catch(() => { if (!cancelled) setRemoteInterventions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewDate, date]);

  // Absences (par date) + pauses (récurrentes) — point 5. La même source que le
  // moteur de réservation en ligne (planning-availability.js), exposée ici pour
  // qu'un technicien absent n'apparaisse plus comme « libre ».
  useEffect(() => {
    let cancelled = false;
    api.get('/planning/pauses')
      .then(r => { if (!cancelled) setPauses(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setPauses([]); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!viewDate) return;
    let cancelled = false;
    api.get('/planning/absences', { params: { date_from: viewDate, date_to: viewDate } })
      .then(r => { if (!cancelled) setAbsences(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [viewDate]);

  // ── Indisponibilités du jour affiché (absences + pauses) → cartes neutres ──
  const { indispoCards, indispoSlots } = useMemo(() => {
    const cards = [];
    const slots = new Set();
    const push = (mecaId, i0, iEnd, label) => {
      if (i0 < 0) i0 = 0;
      if (iEnd > heures.length) iEnd = heures.length;
      if (iEnd <= i0) return;
      cards.push({ mecanicien_id: mecaId, heure_debut: heures[i0], span: iEnd - i0, label });
      for (let k = i0; k < iEnd; k++) slots.add(`${mecaId}-${heures[k]}`);
    };
    const idx = (hm) => {
      if (!hm) return -1;
      const i = heures.indexOf(hm);
      if (i !== -1) return i;
      // pas exactement sur la grille : premier créneau ≥ hm
      const j = heures.findIndex(h => h >= hm);
      return j;
    };
    const ids = new Set(mecasAffiches.map(m => m.id));
    // Absences (l'API filtre déjà sur la date ; double garde par sécurité).
    for (const a of absences) {
      if (!ids.has(a.staff_id)) continue;
      const dd = String(a.date_debut || '').slice(0, 10);
      const df = String(a.date_fin || '').slice(0, 10);
      if (dd && df && !(dd <= viewDate && viewDate <= df)) continue;
      const hd = toHM(a.heure_debut);
      const hf = toHM(a.heure_fin);
      if (!hd && !hf) { push(a.staff_id, 0, heures.length, 'Absent'); continue; }
      const i0 = hd ? idx(hd) : 0;
      const iEnd = hf ? idx(hf) : heures.length;
      push(a.staff_id, i0, iEnd < 0 ? heures.length : iEnd, 'Indispo');
    }
    // Pauses actives ce jour de semaine → pour tous les mécaniciens affichés.
    const jour = viewDate ? JOUR_COL[new Date(`${viewDate}T00:00:00`).getDay()] : null;
    for (const p of pauses) {
      if (!jour || !p[jour]) continue;
      const i0 = idx(toHM(p.heure_debut));
      const iEnd = idx(toHM(p.heure_fin));
      if (i0 < 0) continue;
      for (const m of mecasAffiches) push(m.id, i0, iEnd < 0 ? heures.length : iEnd, 'Pause');
    }
    return { indispoCards: cards, indispoSlots: slots };
  }, [absences, pauses, mecasAffiches, viewDate, heures]);

  if (!date) {
    return <div className="mg-empty">Renseignez une date d'intervention pour voir l'aperçu.</div>;
  }
  if (mecasAffiches.length === 0) {
    return <div className="mg-empty">Aucun mécanicien configuré.</div>;
  }

  const saisis = Array.isArray(currents) ? currents : (current ? [current] : []);
  const sourceInterventions = viewDate === date ? interventions : (remoteInterventions || []);
  const dayInterventions = sourceInterventions.filter(i =>
    i.date_intervention === viewDate && i.heure_debut && i.mecanicien_id
    && !(saisis.length && currentInterventionId
         && (i.id === currentInterventionId || i.parent_intervention_id === currentInterventionId)));
  const fantomes = saisis.filter(g => g && g.mecanicien_id && g.heure_debut && g.date === viewDate);

  const handleDrop = (e, mecaId, heure) => {
    e.preventDefault();
    setDragOverCell(null);
    const id = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!id) return;
    setDraggingId(null);
    const meca = mecasAffiches.find(m => m.id === mecaId);
    onMove(id, {
      heure_debut: heure,
      mecanicien_id: mecaId,
      mecanicien: meca ? getStaffLabel(meca) : null,
      date_intervention: viewDate,
    });
  };

  return (
    <div>
      {ateliers && ateliers.length > 1 && (
        <div className="mg-ateliers" role="group" aria-label="Atelier">
          {ateliers.map(g => (
            <button
              key={g.atelier}
              type="button"
              className={`mg-atelier-btn${g.atelier === atelierSel ? ' on' : ''}`}
              onClick={() => setAtelierSel(g.atelier)}
            >
              {g.atelier}
            </button>
          ))}
        </div>
      )}
      <div className="mg-nav">
        <button type="button" className="mg-nav-btn" onClick={() => setViewDate(v => shiftDate(v, -1))} title="Jour précédent">‹</button>
        <span className="mg-nav-date">{formatDateLabel(viewDate)}</span>
        <button type="button" className="mg-nav-btn" onClick={() => setViewDate(v => shiftDate(v, 1))} title="Jour suivant">›</button>
        {viewDate !== date && (
          <button type="button" className="mg-nav-today" onClick={() => setViewDate(date)}>↺ Date du formulaire</button>
        )}
        {loading && <span className="mg-nav-loading">Chargement…</span>}
      </div>
      <div className="mg-wrapper">
        <div className="mg-grid" style={{ gridTemplateColumns: `44px repeat(${mecasAffiches.length}, minmax(46px, 1fr))` }}>
          <div className="mg-corner" />
          {mecasAffiches.map(m => (
            <div key={m.id} className="mg-col-header" title={getStaffLabel(m)}>{getShortLabel(m)}</div>
          ))}

          {heures.map(h => (
            <React.Fragment key={h}>
              <div className={`mg-time-cell${h.endsWith(':00') ? ' mg-full-hour' : ''}`}>
                {h.endsWith(':00') ? h : ''}
              </div>
              {mecasAffiches.map(meca => {
                const items = dayInterventions.filter(i => i.mecanicien_id === meca.id && i.heure_debut === h);
                const cellKey = `${meca.id}-${h}`;
                const indispo = indispoSlots.has(cellKey);
                const libre = items.length === 0 && !indispo;
                const indispoIci = indispoCards.filter(c => c.mecanicien_id === meca.id && c.heure_debut === h);
                return (
                  <div
                    key={meca.id}
                    className={`mg-slot-cell${dragOverCell === cellKey ? ' mg-drag-over' : ''}${libre && onPickSlot ? ' mg-slot-clickable' : ''}`}
                    onDragOver={(e) => { if (indispo) return; e.preventDefault(); setDragOverCell(cellKey); }}
                    onDragLeave={() => setDragOverCell(prev => (prev === cellKey ? null : prev))}
                    onDrop={(e) => { if (!indispo) handleDrop(e, meca.id, h); }}
                    onClick={() => { if (libre && onPickSlot) onPickSlot(viewDate, h, meca.id); }}
                    title={libre && onPickSlot ? `Choisir ${h} · ${getStaffLabel(meca)} · ${formatDateLabel(viewDate)}` : undefined}
                  >
                    {indispoIci.map((c, ci) => (
                      <div key={`ind-${ci}`} className="mg-card mg-card-indispo" style={{ height: c.span * 16 - 2 }} title={`${c.label} · ${getStaffLabel(meca)}`}>
                        {c.label}
                      </div>
                    ))}
                    {fantomes
                      .filter(g => Number(g.mecanicien_id) === meca.id && g.heure_debut === h)
                      .map((g, gi) => {
                        const gSpan = Math.min(Math.max(1, Math.round((parseFloat(g.duree) || 1) * 4)), heures.length - heures.indexOf(h));
                        return (
                          <div key={`ghost-${gi}`} className="mg-card mg-card-current mg-card-ghost" style={{ height: gSpan * 16 - 2 }}
                            title={`${h} · ${g.label || 'Ce dossier'} (créneau en cours de saisie)`}>
                            {g.label || 'Ce dossier'}
                          </div>
                        );
                      })}
                    {items.map(i => {
                      const clientLabel = [i.client_prenom, i.client_nom].filter(Boolean).join(' ') || i.immatriculation || '—';
                      const isCurrent = currentInterventionId && i.id === currentInterventionId;
                      // Point 4 : une SEULE couleur pour l'occupé — on n'affiche plus le
                      // statut des autres dossiers (inutile pour caler un créneau).
                      const dureeH = parseFloat(i.duree) || 0.25;
                      const span = Math.min(Math.max(1, Math.round(dureeH * 4)), heures.length - heures.indexOf(h));
                      return (
                        <div
                          key={i.id}
                          className={`mg-card mg-card-occ${isCurrent ? ' mg-card-current' : ''}${draggingId === i.id ? ' mg-dragging' : ''}`}
                          style={{ height: span * 16 - 2 }}
                          draggable
                          onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData('text/plain', String(i.id)); e.dataTransfer.effectAllowed = 'move'; setDraggingId(i.id); }}
                          onDragEnd={() => setDraggingId(null)}
                          title={`${h} · ${clientLabel}${i.type_intervention ? ' · ' + i.type_intervention : ''}`}
                        >
                          {clientLabel}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
